import { useState, type ReactNode } from "react";
import type Customer from "@acto/taskpane/core/domain/Customer";
import type { PlanOrigin } from "@acto/taskpane/core/analytics";
import { CustomerPickerSheet } from "@acto/taskpane/field-sales-2/components/patterns/CreateTodoOverlay/CustomerPickerSheet";
import { PlanMeetingSheet } from "./PlanMeetingSheet";

interface PlanMeetingFlow {
  /** Opens the customer picker; the picked customer's plan-meeting sheet uses `date` as default.
   * Stable across renders, safe in memo/effect dependencies. */
  openPicker: (date?: Date | null) => void;
  /** Opens the plan-meeting sheet directly for a known customer. Stable across renders. */
  openPlan: (clientId: string, date?: Date | null) => void;
  /** As `openPicker`, but the sheet fixes the meeting to `date` instead of offering a day grid. */
  openPickerOnDay: (date?: Date | null, origin?: PlanOrigin) => void;
  /** As `openPlan`, but the sheet fixes the meeting to `date` instead of offering a day grid. */
  openPlanOnDay: (customer: Customer, date?: Date | null, origin?: PlanOrigin) => void;
  /** The flow's sheets; the caller must render this node. */
  sheets: ReactNode;
}

/** Customer-picker → plan-meeting sheet flow. */
export function usePlanMeetingFlow(): PlanMeetingFlow {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string | undefined>(undefined);
  const [defaultDate, setDefaultDate] = useState<Date | undefined>(undefined);
  const [lockedDate, setLockedDate] = useState<Date | undefined>(undefined);
  const [planOrigin, setPlanOrigin] = useState<PlanOrigin | undefined>(undefined);

  // The two dates are mutually exclusive, so every opener sets one and clears the other. Nothing
  // clears `planOrigin` on close, so every opener overwrites it too.
  const openPicker = (date?: Date | null) => {
    setDefaultDate(date ?? undefined);
    setLockedDate(undefined);
    setPlanOrigin(undefined);
    setPickerOpen(true);
  };

  const openPickerOnDay = (date?: Date | null, origin?: PlanOrigin) => {
    setDefaultDate(undefined);
    setLockedDate(date ?? undefined);
    setPlanOrigin(origin);
    setPickerOpen(true);
  };

  const openPlan = (planClientId: string, date?: Date | null) => {
    setDefaultDate(date ?? undefined);
    setLockedDate(undefined);
    setPlanOrigin(undefined);
    setClientId(planClientId);
    setPlanOpen(true);
  };

  const openPlanOnDay = (customer: Customer, date?: Date | null, origin?: PlanOrigin) => {
    setDefaultDate(undefined);
    setLockedDate(date ?? undefined);
    setPlanOrigin(origin);
    setClientId(customer.id);
    setClientName(customer.name);
    setPlanOpen(true);
  };

  // `clientId` stays set after closing so the sheet unmounts via Radix's exit animation
  // (toggling `isOpen`), not by being yanked out of the tree.
  const sheets = (
    <>
      <CustomerPickerSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(customer) => {
          setPickerOpen(false);
          setClientId(customer.id);
          setClientName(customer.name);
          setPlanOpen(true);
        }}
      />
      {clientId && (
        <PlanMeetingSheet
          isOpen={planOpen}
          onOpenChange={setPlanOpen}
          clientId={clientId}
          clientName={clientName}
          defaultDate={defaultDate}
          lockedDate={lockedDate}
          planOrigin={planOrigin}
        />
      )}
    </>
  );

  return { openPicker, openPlan, openPickerOnDay, openPlanOnDay, sheets };
}
